import { Flex, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { freightCostsApi } from '@texas/api/endpoints/metadata/freightCostsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { ConfigurationPage } from '@texas/components/configuration/layout/ConfigurationPage';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { useActiveSession } from '@texas/hooks/useSession';
import { CreateFreightCost } from './CreateFreightCost';
import { FreightCostModal } from './FreightCostModal';

export function FreightCosts() {
  const { t } = useTranslation();
  const { currentUser } = useActiveSession();
  const [filter, setFilter] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { data, refetch, loading, error } = useApiResource(
    freightCostsApi.getAll,
  );

  useEffect(() => {
    refetch();
  }, [refetch]);

  const freightCosts = useMemo(() => {
    const normalized = filter.trim().toLowerCase();
    return (data ?? [])
      .filter((item) => !item.archived)
      .filter((item) => item.name.toLowerCase().includes(normalized))
      .sort((left, right) => left.displayOrder - right.displayOrder);
  }, [data, filter]);

  const selected =
    data?.find((freightCost) => freightCost.id === selectedId) ?? null;

  return (
    <ConfigurationPage
      title={t('configuration.navigation.freightCosts')}
      toolbar={
        <SearchFilterInput
          value={filter}
          style="entityDirectory"
          onChange={setFilter}
        />
      }
      action={
        <CreateFreightCost
          organizationId={currentUser.organizationId}
          onCreated={refetch}
        />
      }
    >
      <FreightCostModal
        freightCost={selected}
        isOpen={selected !== null}
        onClose={() => setSelectedId(null)}
        onSaved={refetch}
      />
      <Flex direction="column" gap="4">
        <ErrorDetails error={error} />
        <Table variant="entity-directory">
          <Thead>
            <Tr>
              <Th>{t('general.name')}</Th>
              <Th>{t('general.value')}</Th>
              <Th>{t('branch.displayOrder')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {!loading && freightCosts.length === 0 && (
              <Tr>
                <Td colSpan={3}>{t('general.noData')}</Td>
              </Tr>
            )}
            {freightCosts.map((freightCost) => (
              <Tr
                key={freightCost.id}
                cursor="pointer"
                role="button"
                tabIndex={0}
                onClick={() => setSelectedId(freightCost.id)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  setSelectedId(freightCost.id);
                }}
              >
                <Td>{freightCost.name}</Td>
                <Td>{freightCost.value}</Td>
                <Td>{freightCost.displayOrder}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Flex>
    </ConfigurationPage>
  );
}
